import { useEffect, useState } from "react";
import { http, formatErr } from "../lib/api";
import { Loader2, ShieldCheck } from "lucide-react";
import { RecoveryCodes } from "./RecoveryCodes";

const inp = "w-full px-4 py-3 rounded-lg bg-white/5 border border-white/15 outline-none tracking-[0.3em] text-center text-lg";

// Secondo passaggio dell'accesso: configurazione guidata (QR) oppure verifica del codice Authenticator.
export function MfaStep({ stage, email, password, onDone, onCancel }) {
  const auth = {};
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [codes, setCodes] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (stage !== "enroll") return;
    http.post("/auth/mfa/setup", { password }, auth).then((r) => setSetup(r.data)).catch((e) => setErr(formatErr(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const skip = async () => {
    setBusy(true);
    try { await http.post("/auth/mfa/skip"); await onDone(); } catch (e2) { setErr(formatErr(e2)); }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (stage === "enroll") {
        const { data } = await http.post("/auth/mfa/activate", { code }, auth);
        setCodes({ list: data.recovery_codes });
      } else {
        const { data } = await http.post("/auth/mfa/verify", useRecovery ? { recovery_code: code } : { code }, auth);
        await onDone(data.recovery_left);
      }
    } catch (e2) { setErr(formatErr(e2)); setCode(""); }
    setBusy(false);
  };

  if (codes) {
    return (
      <div data-testid="mfa-recovery-codes">
        <h2 className="font-serif text-2xl mb-2">Codici di recupero</h2>
        <p className="text-sm text-white/70 mb-3">Salvali ora nel tuo gestore di password: vengono mostrati <b>una sola volta</b>. Ogni codice vale per un solo accesso se perdi il telefono.</p>
        <RecoveryCodes codes={codes.list} dark />
        <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-recovery-saved" /> Ho salvato i codici in un luogo sicuro</label>
        <button disabled={!saved} onClick={() => onDone()} data-testid="mfa-recovery-continue" className="w-full py-3 rounded-lg font-medium disabled:opacity-40" style={{ background: "var(--brand-accent)", color: "var(--brand-dark)" }}>Continua</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`mfa-step-${stage}`}>
      <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5" style={{ color: "var(--brand-accent)" }} /><h2 className="font-serif text-2xl">Verifica in due passaggi</h2></div>
      {stage === "enroll" ? (
        <>
          <p className="text-sm text-white/70 mb-3">Consigliata, ma facoltativa: puoi attivarla ora per <b>{email}</b> o in seguito dal profilo. Inquadra il QR con un'app Authenticator (es. Google Authenticator, Microsoft Authenticator, 2FAS), poi inserisci il codice a 6 cifre.</p>
          {setup ? (
            <div className="flex flex-col items-center gap-2 mb-4">
              <img src={`data:image/png;base64,${setup.qr_png_b64}`} alt="QR per app Authenticator" className="w-44 h-44 rounded bg-white p-1" data-testid="mfa-qr" />
              <div className="text-[11px] text-white/60">Non puoi inquadrare? Inserisci la chiave manualmente:</div>
              <code className="text-xs break-all px-2 py-1 rounded bg-white/10" data-testid="mfa-manual-key">{setup.secret.match(/.{1,4}/g).join(" ")}</code>
            </div>
          ) : !err && <Loader2 className="w-5 h-5 animate-spin mx-auto mb-4" />}
        </>
      ) : (
        <p className="text-sm text-white/70 mb-3">{useRecovery ? "Inserisci uno dei codici di recupero (ognuno vale una sola volta)." : "Apri l'app Authenticator e inserisci il codice a 6 cifre per GlowBit Central."}</p>
      )}
      <input value={code} onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" autoFocus
        placeholder={useRecovery ? "XXXX-XXXX" : "000000"} data-testid="mfa-code-input" className={inp} />
      {err && <p className="text-sm text-rose-300 mt-2" data-testid="mfa-error">{err}</p>}
      <button type="submit" disabled={busy || (!useRecovery && code.length !== 6)} data-testid="mfa-submit" className="w-full mt-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-40" style={{ background: "var(--brand-accent)", color: "var(--brand-dark)" }}>
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {stage === "enroll" ? "Attiva e accedi" : "Verifica e accedi"}
      </button>
      <div className="flex justify-between gap-4 mt-4 text-xs text-white/60">
        {stage === "verify" ? <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setErr(""); }} data-testid="mfa-toggle-recovery" className="underline">{useRecovery ? "Usa il codice dell'app" : "Ho perso il telefono: usa un codice di recupero"}</button> : <span />}
        {stage === "enroll"
          ? <button type="button" onClick={skip} disabled={busy} data-testid="mfa-skip" className="underline">Salta per ora</button>
          : <button type="button" onClick={onCancel} data-testid="mfa-cancel" className="underline">Annulla</button>}
      </div>
      <p className="text-[11px] text-white/40 mt-4">Il codice scade dopo pochi secondi e non è riutilizzabile. Dopo 5 codici errati l'accesso si blocca per 15 minuti. Senza telefono e senza codici di recupero contatta l'amministratore.</p>
    </form>
  );
}
